import { useState, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useRoomDetails } from '../hooks/useRoomDetails'
import { useBooking, BookingRequest, ConfirmPaymentRequest } from '../hooks/useBooking'
import { useUser } from '../context/UserContext'
import BookingSuccess from './BookingSuccess'
import RoomDetailsHeader from './roomDetails/RoomDetailsHeader'
import RoomImageGallery from './roomDetails/RoomImageGallery'
import RoomInfoCard from './roomDetails/RoomInfoCard'
import StickyBookingBar from './roomDetails/StickyBookingBar'
import BookingWizard from './roomDetails/BookingWizard'
import VerificationModal from './roomDetails/VerificationModal'
import { normalizeGuestCount, parseGuestCount } from '../utils/guestCount'
import {
  isOfflinePayment,
  isPartialPayment,
  isSuccessfulPaymentStatus,
  isExplicitPaymentFailure,
  logPaymentAmounts,
  getPaymentFailureMessage,
  PAYMENT_METHOD_ONLINE,
  PAYMENT_METHOD_PARTIAL,
} from '../utils/paymentPricing'

const RoomDetails = () => {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { userDetails } = useUser()
  const [selectedImage, setSelectedImage] = useState(0)
  const [isFavorited, setIsFavorited] = useState(false)
  const [showBookingSuccess, setShowBookingSuccess] = useState(false)
  const [showBookingWizard, setShowBookingWizard] = useState(false)
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    nights: 0,
    guests: '1',
    fullName: '',
    phone: '',
    email: '',
    paymentMethod: PAYMENT_METHOD_ONLINE,
    couponCode: ''
  })
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({})
  const [pricing, setPricing] = useState({
    basePrice: 0,
    subtotal: 0,
    discount: 0,
    total: 0
  })

  // Get URL parameters
  const checkin = searchParams.get('checkin') || ''
  const checkout = searchParams.get('checkout') || ''
  const guests = searchParams.get('guests') || ''

  // Fetch room details from API
  const { roomData, loading, error } = useRoomDetails(id || '', checkin, checkout, guests)
  
  // Booking functionality
  const {
    isBooking,
    bookingSuccess,
    createBooking,
    confirmPayment,
    verifyPayment,
    resetBooking,
    patchBookingSuccess,
  } = useBooking()

  const paymentMethods = ['Online', PAYMENT_METHOD_PARTIAL]

  useEffect(() => {
    // Set initial booking data from URL parameters
    setBookingData(prev => ({
      ...prev,
      checkIn: checkin,
      checkOut: checkout,
      guests: normalizeGuestCount(guests)
    }))
  }, [checkin, checkout, guests])

  useEffect(() => {
    // Pre-fill user details if logged in
    if (userDetails) {
      let phoneNumber = userDetails.phoneNumber || ''
      // Remove country code if present
      if (phoneNumber.startsWith('+91')) {
        phoneNumber = phoneNumber.replace('+91', '')
      } else if (phoneNumber.startsWith('91') && phoneNumber.length > 10) {
        phoneNumber = phoneNumber.replace(/^91/, '')
      }
      
      setBookingData(prev => ({
        ...prev,
        fullName: userDetails.displayName || prev.fullName,
        email: userDetails.email || prev.email,
        phone: phoneNumber || prev.phone
      }))
    }
  }, [userDetails])

  useEffect(() => {
    // Update pricing when room data is loaded
    if (roomData) {
      setPricing(prev => ({
        ...prev,
        basePrice: roomData.rent
      }))
    }
  }, [roomData])

  useEffect(() => {
    // Calculate nights and pricing
    if (bookingData.checkIn && bookingData.checkOut && pricing.basePrice > 0) {
      const checkInDate = new Date(bookingData.checkIn)
      const checkOutDate = new Date(bookingData.checkOut)
      const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24))
      
      setBookingData(prev => ({ ...prev, nights }))
      
      const subtotal = nights * pricing.basePrice
      const total = subtotal - pricing.discount
      
      setPricing(prev => ({
        ...prev,
        subtotal,
        total
      }))
    }
  }, [bookingData.checkIn, bookingData.checkOut, pricing.basePrice, pricing.discount])


  const validateForm = () => {
    const errors: {[key: string]: string} = {}
    
    if (!bookingData.checkIn) errors.checkIn = 'Check-in date is required'
    if (!bookingData.checkOut) errors.checkOut = 'Check-out date is required'
    if (bookingData.checkIn && bookingData.checkOut) {
      const checkInDate = new Date(bookingData.checkIn)
      const checkOutDate = new Date(bookingData.checkOut)
      if (checkOutDate <= checkInDate) {
        errors.checkOut = 'Check-out date must be after check-in date'
      }
    }
    
    const guestsNum = parseGuestCount(bookingData.guests)
    if (roomData?.max_guests && roomData.max_guests > 0 && guestsNum > roomData.max_guests) {
      errors.guests = `Maximum ${roomData.max_guests} guests allowed`
    }
    
    if (!bookingData.fullName.trim()) errors.fullName = 'Full name is required'
    if (!bookingData.phone.trim()) errors.phone = 'Phone number is required'
    if (!bookingData.email.trim()) errors.email = 'Email is required'
    if (bookingData.email && !/\S+@\S+\.\S+/.test(bookingData.email)) {
      errors.email = 'Please enter a valid email address'
    }
    if (!bookingData.paymentMethod) errors.paymentMethod = 'Payment method is required'
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleInputChange = (name: string, value: string) => {
    setBookingData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }


  const parseApiErrors = (errorMessage: string) => {
    const fieldErrors: {[key: string]: string} = {}
    
    // Parse validation errors from API
    if (errorMessage.includes('Validation Error:')) {
      const errors = errorMessage.replace('Validation Error: ', '').split(', ')
      errors.forEach(error => {
        const [field, message] = error.split(': ')
        if (field && message) {
          // Map API field names to form field names
          const formField = field === 'check_in' ? 'checkIn' :
                           field === 'check_out' ? 'checkOut' :
                           field === 'full_name' ? 'fullName' :
                           field === 'payment_method' ? 'paymentMethod' :
                           field === 'total_amount' ? 'totalAmount' : field
          
          fieldErrors[formField] = message
        }
      })
    }
    
    return fieldErrors
  }

  const handleBookNow = async (): Promise<{ paymentSessionId?: string | null; bookingNumber?: number; bridgeUrl?: string | null } | void> => {
    if (!validateForm()) {
      // Scroll to first error
      const firstError = Object.keys(formErrors)[0]
      if (firstError) {
        const element = document.querySelector(`[name="${firstError}"]`)
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }

    if (!roomData) return

    try {
      const guestsNum = parseGuestCount(bookingData.guests)
      if (roomData.max_guests && roomData.max_guests > 0 && guestsNum > roomData.max_guests) {
        setFormErrors(prev => ({
          ...prev,
          guests: `Maximum ${roomData.max_guests} guests allowed`
        }))
        return
      }

      const paymentBreakdown = logPaymentAmounts(
        bookingData.paymentMethod,
        pricing.total,
        'Create booking'
      )

      const bookingRequest: BookingRequest = {
        room_id: roomData.id,
        check_in: bookingData.checkIn,
        check_out: bookingData.checkOut,
        guests: guestsNum,
        full_name: bookingData.fullName,
        phone: bookingData.phone,
        email: bookingData.email,
        payment_method: bookingData.paymentMethod,
        coupon_code: bookingData.couponCode || undefined,
        total_amount: pricing.total,
        payable_amount: paymentBreakdown.payNow,
        ...(isPartialPayment(bookingData.paymentMethod)
          ? { service_charge: paymentBreakdown.serviceFeeCharged }
          : {}),
        special_requests: undefined,
      }

      const booking = await createBooking(bookingRequest)
      console.log('[Create booking] API response amounts', {
        bookingNumber: booking.booking_number,
        grandTotal: booking.grand_total,
        amountDueNow: booking.amount_due_now,
        balanceDueAtCheckin: booking.balance_due_at_checkin,
        partialServiceFee: booking.partial_service_fee,
      })
      const isOfflineMethod = isOfflinePayment(bookingData.paymentMethod)

      if (!isOfflineMethod) {
        const bookingNumber = Number(
          booking.booking_number ?? (booking as { bookingNumber?: number }).bookingNumber
        )
        if (!Number.isFinite(bookingNumber) || bookingNumber <= 0) {
          throw new Error('Booking succeeded but booking number was missing. Please contact support.')
        }

        if (booking.bridge_url || booking.checkout_mode === 'partner_redirect') {
          sessionStorage.setItem('aonlastay_booking_number', String(bookingNumber))
          return { bookingNumber, bridgeUrl: booking.bridge_url || null }
        }

        if (!booking.payment_session_id) {
          throw new Error('Unable to start online payment. Please try again in a moment.')
        }
        sessionStorage.removeItem('cashfree_payment_session_id')
        sessionStorage.removeItem('cashfree_booking_number')
        sessionStorage.removeItem('aonlastay_booking_number')
        sessionStorage.setItem('cashfree_payment_session_id', booking.payment_session_id)
        sessionStorage.setItem('cashfree_booking_number', String(bookingNumber))
        sessionStorage.setItem('aonlastay_booking_number', String(bookingNumber))
        return { paymentSessionId: booking.payment_session_id, bookingNumber }
      }

      setShowBookingWizard(false)
      setShowBookingSuccess(true)
      
      // Scroll to top when booking is successful
      window.scrollTo({ top: 0, behavior: 'smooth' })
      
    } catch (error) {
      console.error('Booking failed:', error)
      sessionStorage.removeItem('cashfree_payment_session_id')
      sessionStorage.removeItem('cashfree_booking_number')
      sessionStorage.removeItem('aonlastay_booking_number')

      // Parse API errors and set form errors
      if (error instanceof Error) {
        const apiErrors = parseApiErrors(error.message)
        if (Object.keys(apiErrors).length > 0) {
          setFormErrors(prev => ({ ...prev, ...apiErrors }))

          // Scroll to first API error field
          const firstErrorField = Object.keys(apiErrors)[0]
          const element = document.querySelector(`[name="${firstErrorField}"]`)
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
      throw error
    }
  }

  const handlePaymentComplete = async ({
    bookingNumber,
    paymentId,
    paymentStatus
  }: {
    bookingNumber?: number
    paymentId?: string
    paymentStatus?: string
  }) => {
    if (isExplicitPaymentFailure(paymentStatus) && !paymentId) {
      setFormErrors((prev) => ({
        ...prev,
        paymentMethod: getPaymentFailureMessage(paymentStatus),
      }))
      throw new Error('Payment not completed')
    }

    try {
      const partialNote = isPartialPayment(bookingData.paymentMethod)
        ? `Partial payment confirmed online. Balance due at check-in.`
        : 'Confirmed from online checkout modal'

      let order

      if (bookingNumber && paymentId && isSuccessfulPaymentStatus(paymentStatus)) {
        order = await confirmPayment(bookingNumber, {
          payment_id: paymentId,
          payment_status: (paymentStatus || 'success') as ConfirmPaymentRequest['payment_status'],
          payment_method: bookingData.paymentMethod,
          notes: partialNote,
        })
      } else if (bookingNumber) {
        order = await verifyPayment(bookingNumber)
      } else {
        throw new Error('Booking number is missing for payment confirmation.')
      }

      if (!isSuccessfulPaymentStatus(order.payment_status)) {
        setFormErrors((prev) => ({
          ...prev,
          paymentMethod: getPaymentFailureMessage(order.payment_status),
        }))
        throw new Error('Payment not verified')
      }

      patchBookingSuccess({
        amount_due_now: order.amount,
        payment_status: isPartialPayment(bookingData.paymentMethod) ? 1 : 2,
      })

      sessionStorage.removeItem('cashfree_payment_session_id')
      sessionStorage.removeItem('cashfree_booking_number')
      sessionStorage.removeItem('aonlastay_booking_number')
      setShowBookingWizard(false)
      setShowBookingSuccess(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.error('Payment confirmation sync failed:', error)
      setFormErrors((prev) => ({
        ...prev,
        paymentMethod: prev.paymentMethod || getPaymentFailureMessage(paymentStatus),
      }))
      throw error
    }
  }

  const handlePaymentFailed = (_message: string) => {
    setFormErrors((prev) => ({ ...prev, paymentMethod: '' }))
  }

  // Show booking success screen
  if (showBookingSuccess && bookingSuccess) {
    return (
      <BookingSuccess
        booking={bookingSuccess}
        onBackToHome={() => {
          resetBooking()
          setShowBookingSuccess(false)
          navigate('/')
        }}
        onNewBooking={() => {
          resetBooking()
          setShowBookingSuccess(false)
          // Reset form
          setBookingData({
            checkIn: '',
            checkOut: '',
            nights: 0,
            guests: '1',
            fullName: '',
            phone: '',
            email: '',
            paymentMethod: PAYMENT_METHOD_ONLINE,
            couponCode: ''
          })
          setFormErrors({})
        }}
      />
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading room details...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl">⚠️</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Room</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // No room data
  if (!roomData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-gray-500 text-2xl">🏠</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Room Not Found</h2>
          <p className="text-gray-600 mb-4">The room you're looking for doesn't exist.</p>
          <button 
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-20 sm:pb-24">
      {/* Header */}
      <RoomDetailsHeader
        roomId={roomData.room_id || 'N/A'}
        isFavorited={isFavorited}
        onBack={() => window.history.back()}
        onFavorite={() => setIsFavorited(!isFavorited)}
        onShare={() => {
          if (navigator.share) {
            navigator.share({
              title: roomData.title,
              text: `Check out this room: ${roomData.title}`,
              url: window.location.href
            })
          }
        }}
      />

      {/* Room gallery + details */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 items-start">
          <div className="animate-slide-in-left lg:sticky lg:top-20">
            <RoomImageGallery
              images={roomData.images}
              featuredImg={roomData.featured_img || ''}
              title={roomData.title}
              selectedImage={selectedImage}
              rating={roomData.rating}
              categoryName={roomData.category?.name || undefined}
              rent={roomData.rent}
              onImageSelect={setSelectedImage}
            />
          </div>

          <div className="animate-slide-in-right">
            <RoomInfoCard roomData={{
              ...roomData,
              category: roomData.category || undefined,
              phone: roomData.phone || undefined,
              email: roomData.email || undefined,
              address: roomData.address || undefined,
              vendor: roomData.vendor || undefined,
              reviews: roomData.reviews.map(r => ({
                ...r,
                created_at: r.created_at || undefined
              }))
            }} />
          </div>
        </div>
      </div>

      {/* Sticky Booking Bar */}
      <StickyBookingBar
        checkIn={bookingData.checkIn}
        checkOut={bookingData.checkOut}
        nights={bookingData.nights}
        guests={bookingData.guests}
        rent={roomData.rent}
        total={pricing.total}
        onBookClick={() => {
          // Check if user is logged in
          if (userDetails) {
            setShowBookingWizard(true)
          } else {
            setShowVerificationModal(true)
          }
        }}
      />

      {/* Verification Modal */}
      <VerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        onVerified={() => {
          setShowVerificationModal(false)
          setShowBookingWizard(true)
        }}
      />

      {/* Booking Wizard Modal */}
      {showBookingWizard && (
        <BookingWizard
          roomData={roomData}
          bookingData={bookingData}
          pricing={pricing}
          formErrors={formErrors}
          paymentMethods={paymentMethods}
          isBooking={isBooking}
          onChange={handleInputChange}
          onConfirm={handleBookNow}
          onPaymentComplete={handlePaymentComplete}
          onPaymentFailed={handlePaymentFailed}
          onClose={() => setShowBookingWizard(false)}
        />
      )}
    </div>
  )
}

export default RoomDetails
